import {
  AppBar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  Alert,
  Chip,
  useMediaQuery,
} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MenuIcon from '@mui/icons-material/Menu';
import StoreSelect from 'components/StoreSelect';
import { makeStyles } from 'tss-react/mui';
import { CSSProperties, useContext, useEffect, useMemo, useState } from 'react';
import { grey } from '@mui/material/colors';
import { Link, useLocation } from 'react-router-dom';
import { CURRENT_ORDERS_PATH, SCREENS } from 'routing';
import { isCurrentOpeningHour, formatPickupDatetimes, formatPickupTime, dateToBackendDateString } from 'utils';
import StoreContext from 'contexts/storeContext';
import { Notifications, AccessTime, Storefront, ShoppingCart, HelpOutline, Star } from '@mui/icons-material';
import NotificationsContext from 'contexts/notificationsContext';
import { useMountLogs } from 'hooks';
import ApiContext from 'contexts/apiContext';

const APPBAR_WIDTH = 230;

const useStyles = makeStyles()((theme) => ({
  toolbar: {
    paddingRight: 24,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    minHeight: '64px',
    overflow: 'hidden',
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 10px',
    ...(theme.mixins.toolbar as CSSProperties),
  },
  appBar: {
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: APPBAR_WIDTH,
    width: `calc(100% - ${APPBAR_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: APPBAR_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: 100,
    }),
    willChange: 'width',
  },
  drawerPaperClosed: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: 100,
    }),
    willChange: 'width',
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
    [theme.breakpoints.up('sm')]: {
      '&:hover': {
        width: APPBAR_WIDTH, // expand on hover only on desktop
      },
    },
  },
  link: {
    color: 'inherit',
    textDecoration: 'inherit',
  },
  selectedItem: {
    backgroundColor: grey[200],
  },
  menuText: {
    fontWeight: 500,
    fontSize: 14,
  },
  inactiveStoreText: {
    marginRight: theme.spacing(5),
    opacity: 0.75,
  },
  pickupStatusChip: {
    marginRight: theme.spacing(2),
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    '& .MuiChip-icon': {
      color: 'white',
      marginLeft: theme.spacing(1.5),
    },
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '200px',
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      minWidth: '40px',
      paddingLeft: 0,
      paddingRight: 0,
      '& .MuiChip-icon': {
        marginLeft: 0,
        marginRight: 0,
      },
      '& .MuiChip-label': {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
  },
  pickupStatusChipOpen: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  pickupStatusChipClosed: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  pickupStatusChipNoHours: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

type Props = {
  logout: () => void;
  basePath: string;
};

export default function HeaderAndMenu({ logout, basePath }: Props) {
  const { classes, cx } = useStyles();
  const { store, hasPermission } = useContext(StoreContext);
  const { api } = useContext(ApiContext);
  const { unreadNotificationsCount, orderNotification } = useContext(NotificationsContext);
  const currentPath = useLocation().pathname;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isTodayClosed, setIsTodayClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:700px)');

  useMountLogs('HeaderAndDrawer');

  useEffect(() => {
    // Check if store is open once a minute (60000ms)
    const updateIsOpen = () => setIsOpen(isCurrentOpeningHour(store.next_dated_opening_hour));
    updateIsOpen(); // Check on mount instead of waiting a minute for the first update
    const interval = setInterval(updateIsOpen, 60000);
    return () => clearInterval(interval); // Remove interval on unmount
  }, [store.next_dated_opening_hour]);

  useEffect(() => {
    api.closedDates.list_not_paginated(store.id).then((res) => {
      const today = dateToBackendDateString(new Date());
      setIsTodayClosed(res.data.some((closedDate) => closedDate.date === today));
      setIsLoading(false);
    });
  }, [api, store.id]);

  const pickupStatus = useMemo(() => {
    // Don't calculate status while loading
    if (isLoading) return null;

    // Today is closed
    if (isTodayClosed) {
      return {
        label: 'HOY CERRADO',
        icon: <ShoppingCart fontSize='small' />,
        className: classes.pickupStatusChipNoHours,
        helperText: 'Haz click aquí para modificar tus fechas de cierre',
        link: `${basePath}/times`,
      };
    }

    // Is inside pickup time
    if (isOpen) {
      return {
        label: store.next_dated_opening_hour ? `RETIRO ABIERTO: ${formatPickupTime(store.next_dated_opening_hour)}` : 'RETIRO ABIERTO',
        icon: <Storefront fontSize='small' />,
        className: classes.pickupStatusChipOpen,
        link: `${basePath}/times`,
      };
    }

    // Is outside pickup time but its not marked as closed today
    if (store.next_dated_opening_hour) {
      return {
        label: `PRÓXIMO: ${formatPickupDatetimes([store.next_dated_opening_hour])}`,
        icon: <AccessTime fontSize='small' />,
        className: classes.pickupStatusChipClosed,
        link: `${basePath}/times`,
      };
    }

    // No opening hours
    return {
      label: 'SIN HORARIOS',
      icon: <HelpOutline fontSize='small' />,
      className: classes.pickupStatusChipNoHours,
      helperText: 'Haz click aquí para agregar tus horarios de retiro',
      link: `${basePath}/times`,
    };
  }, [isTodayClosed, isOpen, store.next_dated_opening_hour, basePath, classes, isLoading]);

  return (
    <>
      <AppBar className={cx(classes.appBar, drawerOpen && classes.appBarShift)}>
        <Toolbar className={classes.toolbar}>
          <Box display='flex' alignItems='center' justifyContent='flex-start' sx={{ minWidth: 0 }}>
            <IconButton
              edge='start'
              color='inherit'
              onClick={() => setDrawerOpen(true)}
              className={cx(classes.menuButton, drawerOpen && classes.menuButtonHidden)}
              size='large'
            >
              <MenuIcon />
            </IconButton>
            {!store.is_active && (
              <Alert
                severity='warning'
                sx={{
                  maxWidth: { xs: '120px', sm: '400px', md: '600px' },
                  py: { xs: '1px', sm: '2px' },
                  px: { xs: '6px', sm: '12px' },
                  margin: { xs: '2px', sm: '8px' },
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  minHeight: { xs: '28px', sm: 'auto' },
                  '& .MuiAlert-message': {
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    lineHeight: 1.1,
                    padding: 0,
                  },
                  '& .MuiAlert-icon': {
                    fontSize: { xs: '16px', sm: '20px' },
                    marginRight: { xs: '4px', sm: '8px' },
                  },
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>La tienda esta inactiva, para activarla escríbenos a través de </Box>
                <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Tienda inactiva - </Box>
                <MuiLink
                  className={classes.link}
                  href={`https://wa.me/${store.country.whatsapp_support_number}`}
                  target='_blank'
                  underline='always'
                >
                  <b>WhatsApp</b>
                </MuiLink>
              </Alert>
            )}
          </Box>
          <Box display='flex' flex={1} alignItems='center' justifyContent='flex-end' sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Link to={'notifications'}>
              <IconButton color='secondary' title='Notificaciones'>
                <Badge badgeContent={unreadNotificationsCount} color='warning' max={9} overlap='circular'>
                  <Notifications />
                </Badge>
              </IconButton>
            </Link>
            {!isMobile && store.is_promoted && (
              <Tooltip title="Tu tienda aparece destacada en el inicio de la app" placement="bottom" arrow>
                <Star sx={{ color: '#FFD700', fontSize: 28, mx: 1 }} />
              </Tooltip>
            )}
            {pickupStatus &&
              (pickupStatus.helperText ? (
                <Link to={pickupStatus.link!} className={classes.link}>
                  <Tooltip title={pickupStatus.helperText} placement='bottom' enterTouchDelay={0}>
                    <Chip
                      icon={pickupStatus.icon}
                      label={isMobile ? '' : pickupStatus.label}
                      className={cx(classes.pickupStatusChip, pickupStatus.className)}
                      variant='filled'
                      clickable
                    />
                  </Tooltip>
                </Link>
              ) : (
                <Link to={pickupStatus.link!} className={classes.link}>
                  <Tooltip title={isMobile ? pickupStatus.label : ''} placement='bottom' enterTouchDelay={0}>
                    <Chip
                      icon={pickupStatus.icon}
                      label={isMobile ? '' : pickupStatus.label}
                      className={cx(classes.pickupStatusChip, pickupStatus.className)}
                      variant='filled'
                    />
                  </Tooltip>
                </Link>
              ))}
            <StoreSelect />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant='permanent'
        classes={{
          paper: cx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClosed),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(false)} size='large'>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          {SCREENS.filter((s) => !s.notShownInDrawer)
            .filter((item) => !item.permission || hasPermission(item.permission))
            .map((item) => {
              const path = basePath + '/' + item.path;
              return (
                <Link to={path} key={item.name} className={classes.link}>
                  <ListItem className={currentPath.startsWith(path) ? classes.selectedItem : ''}>
                    <ListItemIcon>
                      {item.path === CURRENT_ORDERS_PATH && orderNotification ? (
                        <Badge color='warning' variant='dot'>
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText primary={<Typography className={classes.menuText}>{item.name}</Typography>} />
                  </ListItem>
                </Link>
              );
            })}
          <ListItem onClick={logout} sx={{ cursor: 'pointer' }}>
            <ListItemIcon>
              <MeetingRoomIcon color='primary' />
            </ListItemIcon>
            <ListItemText primary={<Typography className={classes.menuText}>Cerrar sesión</Typography>} />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
